import {
  BookOpen,
  CalendarDays,
  Layers,
  Loader2,
  Menu,
  Search,
  ShoppingCart,
  User,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { getAllValidProducts } from '../../../api/action/products';
import { setSelectedProduct } from '../../../api/reducers/selectedProductReducer';
import {
  getEducationPageCacheKey,
  readEducationPageCache,
} from '../../../domains/education/utils/educationPageCache';
import { useTemplateAuthState } from '../../../template/TemplateAuthState.jsx';
import { useAuthSession } from '../../../auth/useAuthSession.js';
import { useSiteContext } from '../../../context/SiteContext.jsx';
import { KRUMAGO } from '../../s3-assets/Krumago';

const NAV_ITEMS = [
  { label: 'Home', path: '/' },
  { label: 'Courses', path: '/course' },
  { label: 'About', path: '/about' },
  { label: 'Contact', path: '/contact' },
];

const RESULT_ACCENTS = {
  category: {
    color: '#1e40af',
    softBg: '#eff6ff',
    border: '#bfdbfe',
    badge: '#3b82f6',
    label: 'Category',
  },
  'scheduled-product': {
    color: '#059669',
    softBg: '#ecfdf5',
    border: '#a7f3d0',
    badge: '#10b981',
    label: 'Scheduled',
  },
  product: {
    color: '#1f2937',
    softBg: '#f8fafc',
    border: '#e2e8f0',
    badge: '#6b7280',
    label: 'Course',
  },
};

const debounce = (func, delay) => {
  let debounceTimer;

  const debounced = (...args) => {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => func(...args), delay);
  };

  debounced.cancel = () => clearTimeout(debounceTimer);
  return debounced;
};

const getResultAccent = (type) => RESULT_ACCENTS[type] || RESULT_ACCENTS.product;

const SearchResultIcon = ({ type }) => {
  const Icon = type === 'category'
    ? Layers
    : type === 'scheduled-product'
      ? CalendarDays
      : BookOpen;

  return <Icon size={16} strokeWidth={2} />;
};

const normalizeProductList = (result) => (
  Array.isArray(result?.data)
    ? result.data
    : Array.isArray(result)
      ? result
      : []
);

const buildSearchIndexes = (products = []) => {
  const categoriesMap = new Map();
  const scheduledProductsMap = new Map();

  products.forEach(product => {
    if (product.category && product.category.primaryId && product.category.name) {
      if (!categoriesMap.has(product.category.primaryId)) {
        categoriesMap.set(product.category.primaryId, {
          type: 'category',
          primaryId: product.category.primaryId,
          name: product.category.name,
          productCount: 1
        });
      } else {
        const category = categoriesMap.get(product.category.primaryId);
        category.productCount += 1;
      }
    }

    if (product.productType === 'SCHEDULED' || product.isScheduled) {
      if (!scheduledProductsMap.has(product.primaryId)) {
        scheduledProductsMap.set(product.primaryId, {
          type: 'scheduled-product',
          primaryId: product.primaryId,
          name: product.name,
          categoryName: product.category?.name || '',
          isScheduled: true
        });
      }
    }
  });

  return {
    categories: Array.from(categoriesMap.values()),
    scheduledProducts: Array.from(scheduledProductsMap.values()),
  };
};

const EducationDomainHeader = () => {
  const [openMenu, setOpenMenu] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [cartItemCount, setCartItemCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredResults, setFilteredResults] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [uniqueCategories, setUniqueCategories] = useState([]);
  const [uniqueScheduledProducts, setUniqueScheduledProducts] = useState([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const searchContainerRef = useRef(null);
  const inputRef = useRef(null);
  const lastProcessedQuery = useRef('');
  const isMouseDownRef = useRef(false);
  const catalogLoadPromiseRef = useRef(null);
  const templateAuth = useTemplateAuthState();

  const { isAuthenticated } = useAuthSession();
  const siteContext = useSiteContext();
  const reduxCompany = useSelector((state) => state.companyReducer.company);
  const companyDetails = siteContext?.company || reduxCompany;
  const companyPrimaryId = companyDetails?.primaryId;
  const logo = companyDetails?.logoImageContent?.displayLink;
  // Derive this during render so the first committed frame already uses the
  // resolved tenant logo. An effect-backed state value briefly rendered the
  // Academy fallback while the selected company's logo was being copied over.
  const logoUrl = logo
    ? (logo.includes('/MicrosoftTeams-image.png') ? KRUMAGO.IMAGES.AVPS : logo)
    : '';
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const currentPath = location.pathname;

  useEffect(() => {
    const updateCartItemCount = () => {
      try {
        const cartProducts = JSON.parse(localStorage.getItem('cartProducts')) || [];
        setCartItemCount(Array.isArray(cartProducts) ? cartProducts.length : 0);
      } catch {
        setCartItemCount(0);
      }
    };

    updateCartItemCount();
    const intervalId = setInterval(updateCartItemCount, 1000);
    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    setIsLoggedIn(isAuthenticated || templateAuth.isLoggedIn);
  }, [isAuthenticated, templateAuth.isLoggedIn]);

  useEffect(() => {
    catalogLoadPromiseRef.current = null;
    setFilteredResults([]);
    lastProcessedQuery.current = '';

    if (!companyPrimaryId) {
      setAllProducts([]);
      setUniqueCategories([]);
      setUniqueScheduledProducts([]);
      return;
    }

    const cachedProducts = readEducationPageCache(getEducationPageCacheKey('products', { companyId: companyPrimaryId }))?.products
      || readEducationPageCache(getEducationPageCacheKey('home', { companyId: companyPrimaryId }))?.categoryProducts
      || [];

    if (cachedProducts.length) {
      const indexes = buildSearchIndexes(cachedProducts);
      setAllProducts(cachedProducts);
      setUniqueCategories(indexes.categories);
      setUniqueScheduledProducts(indexes.scheduledProducts);
    }
  }, [companyPrimaryId]);

  const loadAllProducts = useCallback(async (company) => {
    if (!company) return [];
    if (catalogLoadPromiseRef.current) return catalogLoadPromiseRef.current;

    const loadPromise = (async () => {
      const cachedProducts = readEducationPageCache(getEducationPageCacheKey('products', { companyId: company }))?.products
        || readEducationPageCache(getEducationPageCacheKey('home', { companyId: company }))?.categoryProducts
        || [];

      if (cachedProducts.length) {
        const indexes = buildSearchIndexes(cachedProducts);
        setAllProducts(cachedProducts);
        setUniqueCategories(indexes.categories);
        setUniqueScheduledProducts(indexes.scheduledProducts);
        setFilteredResults([]);
        return cachedProducts;
      }

      setLoading(true);

      const productsResult = await dispatch(getAllValidProducts(company));
      const mergedProducts = normalizeProductList(productsResult);

      setAllProducts(mergedProducts);
      setFilteredResults([]);

      const indexes = buildSearchIndexes(mergedProducts);
      setUniqueCategories(indexes.categories);
      setUniqueScheduledProducts(indexes.scheduledProducts);
      return mergedProducts;
    })();

    catalogLoadPromiseRef.current = loadPromise;

    try {
      return await loadPromise;
    } catch (error) {
      setSnackbarMessage(error?.message || "Failed to load products");
      setSnackbarOpen(true);
      return [];
    } finally {
      setLoading(false);
      catalogLoadPromiseRef.current = null;
    }
  }, [dispatch]);

  const ensureSearchCatalog = useCallback(() => {
    if (companyPrimaryId && allProducts.length === 0) {
      loadAllProducts(companyPrimaryId);
    }
  }, [allProducts.length, companyPrimaryId, loadAllProducts]);

  const searchInData = useCallback((query) => {
    if (!query || query.trim() === '' || query === lastProcessedQuery.current) {
      if (query.trim() === '') {
        setFilteredResults([]);
        setIsSearchOpen(false);
      }
      return;
    }

    lastProcessedQuery.current = query;
    const searchTerm = query.toLowerCase().trim();

    const productMatches = allProducts.filter(product => {
      const productName = product.name?.toLowerCase() || '';
      const categoryName = product.category?.name?.toLowerCase() || '';
      const brandName = product.brand?.toLowerCase() || '';

      return productName.includes(searchTerm) ||
        categoryName.includes(searchTerm) ||
        brandName.includes(searchTerm);
    }).map(product => ({
      ...product,
      type: product.productType === 'SCHEDULED' || product.isScheduled ? 'scheduled-product' : 'product',
      displayName: product.name,
      displayImage: product.imageContents?.[0]?.displayLink || ''
    }));

    const categoryMatches = uniqueCategories.filter(category => {
      const categoryName = category.name?.toLowerCase() || '';
      return categoryName.includes(searchTerm);
    }).map(category => ({
      ...category,
      type: 'category',
      displayName: `${category.name} - Category (${category.productCount} courses)`
    }));

    const scheduledProductMatches = uniqueScheduledProducts.filter(sp => {
      const productName = sp.name?.toLowerCase() || '';
      const categoryName = sp.categoryName?.toLowerCase() || '';
      return productName.includes(searchTerm) || categoryName.includes(searchTerm);
    }).map(sp => ({
      ...sp,
      displayImage: ''
    }));

    const combinedResults = [
      ...categoryMatches,
      ...scheduledProductMatches,
      ...productMatches.filter(p => p.type !== 'scheduled-product')
    ];

    setFilteredResults(combinedResults.slice(0, 6));
    setIsSearchOpen(combinedResults.length > 0);
    setHighlightedIndex(-1);
  }, [allProducts, uniqueCategories, uniqueScheduledProducts]);

  useEffect(() => {
    if (searchQuery.trim() === '' || allProducts.length === 0) return;
    lastProcessedQuery.current = '';
    searchInData(searchQuery);
  }, [allProducts.length, searchInData, searchQuery]);

  const debouncedSearch = useMemo(() => debounce((newValue) => {
    if (newValue && newValue.trim() !== '') {
      searchInData(newValue);
    } else {
      setFilteredResults([]);
      setIsSearchOpen(false);
      lastProcessedQuery.current = '';
    }
  }, 200), [searchInData]);

  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  const handleInputChange = (event) => {
    const newValue = event.target.value || '';
    setSearchQuery(newValue);
    if (newValue.trim() !== '') {
      ensureSearchCatalog();
    }
    debouncedSearch(newValue);
  };

  const handleKeyDown = (event) => {
    if (!isSearchOpen && event.key !== 'Enter') return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (filteredResults.length > 0) {
          const newIndex = highlightedIndex < filteredResults.length - 1 ? highlightedIndex + 1 : 0;
          setHighlightedIndex(newIndex);
        }
        break;

      case 'ArrowUp':
        event.preventDefault();
        if (filteredResults.length > 0) {
          const newIndex = highlightedIndex > 0 ? highlightedIndex - 1 : filteredResults.length - 1;
          setHighlightedIndex(newIndex);
        }
        break;

      case 'Enter':
        event.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredResults.length) {
          handleSuggestionSelect(filteredResults[highlightedIndex]);
        } else if (searchQuery.trim() !== '') {
          navigate(`/course?search=${encodeURIComponent(searchQuery.trim())}`);
          handleClearSearch();
        }
        break;

      case 'Escape':
        event.preventDefault();
        setIsSearchOpen(false);
        setHighlightedIndex(-1);
        break;

      default:
        break;
    }
  };

  const handleSuggestionSelect = (selectedItem) => {
    if (!selectedItem) return;

    setIsSearchOpen(false);
    setHighlightedIndex(-1);
    setSearchQuery('');
    setFilteredResults([]);
    lastProcessedQuery.current = '';

    if (selectedItem.type === 'category') {
      navigate(`/course?category=${selectedItem.primaryId}`);
    } else {
      dispatch(setSelectedProduct(selectedItem));
      navigate('/course-detail');
    }
  };

  const handleSearchClick = () => {
    if (searchQuery.trim() !== '') {
      ensureSearchCatalog();
      const query = searchQuery.trim();
      navigate(`/course?search=${encodeURIComponent(query)}`);
      handleClearSearch();
    }
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setFilteredResults([]);
    setIsSearchOpen(false);
    setHighlightedIndex(-1);
    lastProcessedQuery.current = '';
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleSearchFocus = () => {
    setIsSearchFocused(true);
    ensureSearchCatalog();
    if (searchQuery.trim() !== '' && filteredResults.length > 0) {
      setIsSearchOpen(true);
    }
  };

  const handleSearchBlur = () => {
    setIsSearchFocused(false);

    if (isMouseDownRef.current) {
      isMouseDownRef.current = false;
      return;
    }

    setTimeout(() => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(document.activeElement)) {
        setIsSearchOpen(false);
        setHighlightedIndex(-1);
      }
    }, 150);
  };

  const handleNavigation = (path) => {
    navigate(path);
    setOpenMenu(false);
  };

  const handleSignInClick = () => {
    navigate('/sign-in');
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  useEffect(() => {
    if (!snackbarOpen) return undefined;

    const timeout = setTimeout(() => setSnackbarOpen(false), 5000);
    return () => clearTimeout(timeout);
  }, [snackbarOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setIsSearchOpen(false);
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  if (currentPath.startsWith('/exam-management')) {
    return null;
  }

  const handleMouseDown = () => {
    isMouseDownRef.current = true;
  };

  const handleMouseUp = () => {
    isMouseDownRef.current = false;
  };

  const navButtonStyle = (path) => ({
    color: 'var(--education-primary, #746EAC)',
    fontWeight: 600,
    fontSize: '14px',
    cursor: 'pointer',
    padding: '6px 12px',
    borderRadius: '6px',
    border: 'none',
    textDecoration: 'none',
    transition: 'all 0.2s ease',
    background: currentPath === path ? '#f3f4f6' : 'transparent',
    fontFamily: 'inherit'
  });

  const mobileNavButtonStyle = (path) => ({
    background: currentPath === path ? '#f3f4f6' : 'transparent',
    border: 'none',
    borderRadius: '6px',
    color: 'var(--education-primary, #746EAC)',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    fontFamily: 'inherit',
    fontSize: '14px',
    fontWeight: 600,
    marginBottom: '4px',
    padding: '10px 14px',
    textAlign: 'left',
    transition: 'all 0.2s ease',
    width: '100%'
  });

  const SearchDropdown = () => (
    <div
      id="search-results-container"
      data-testid="search-results-container"
      className="search-results-container absolute left-0 right-0 top-[calc(100%+1px)] z-[9999] max-h-80 overflow-y-auto rounded-lg border border-gray-200 bg-white"
      style={{
        boxShadow: '0 6px 20px rgba(0, 0, 0, 0.12)',
        display: isSearchOpen ? 'block' : 'none',
      }}
    >
      {filteredResults.length > 0 ? (
        filteredResults.map((option, index) => {
          const accent = getResultAccent(option.type);

          return (
            <button
              type="button"
              key={`${option.type}-${option.primaryId || option.id || index}`}
              id={`search-item-${index}`}
              data-testid={`search-item-${index}`}
              className={`search-item ${option.type === 'category' ? 'search-category' : 'search-product'} flex w-full items-center px-3.5 py-2.5 text-left transition-colors hover:bg-gray-50`}
              onMouseDown={handleMouseDown}
              onMouseUp={handleMouseUp}
              onClick={() => handleSuggestionSelect(option)}
              onMouseEnter={() => setHighlightedIndex(index)}
              style={{
                backgroundColor: index === highlightedIndex ? '#f9fafb' : '#ffffff',
                border: 'none',
                borderBottom: index < filteredResults.length - 1 ? '1px solid #f3f4f6' : 'none',
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              <span
                id={`search-item-image-${index}`}
                className="search-item-image relative mr-3 flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-md"
                style={{
                  background: accent.softBg,
                  border: `1px solid ${accent.border}`,
                  color: accent.color,
                }}
              >
                <SearchResultIcon type={option.type} />
                {option.displayImage && (
                  <img
                    src={option.displayImage}
                    alt={option.name}
                    className="absolute inset-0 h-full w-full object-cover"
                    onError={(event) => {
                      event.currentTarget.style.display = 'none';
                    }}
                  />
                )}
              </span>

              <span className="min-w-0 flex-1">
                <span
                  id={`search-item-title-${index}`}
                  className="search-item-title mb-0.5 flex items-center gap-1.5"
                >
                  <span
                    className="truncate text-[13px] font-semibold"
                    style={{ color: accent.color }}
                  >
                    {option.type === 'category' ? `${option.name} ` :
                      option.type === 'scheduled-product' ? option.name :
                        option.displayName || option.name || ''}
                  </span>
                  <span
                    id={`search-item-badge-${index}`}
                    className="search-item-badge shrink-0 whitespace-nowrap rounded-md px-1.5 py-px text-[9px] font-semibold uppercase text-white"
                    style={{ backgroundColor: accent.badge, letterSpacing: '0.3px' }}
                  >
                    {accent.label}
                  </span>
                </span>

                <span
                  id={`search-item-description-${index}`}
                  className="search-item-description block truncate text-[11px] font-normal text-gray-500"
                >
                  {option.type === 'category' ? `${option.productCount} courses available` :
                    option.type === 'scheduled-product' ? 'Scheduled course - Check dates' :
                      option.category?.name ? `Category: ${option.category.name}` : 'Course details'}
                </span>
              </span>
            </button>
          );
        })
      ) : searchQuery.trim() !== '' ? (
        <div
          id="search-no-results"
          data-testid="search-no-results"
          className="search-no-results rounded-lg px-4 py-6 text-center text-[13px] text-gray-500"
        >
          <Search size={28} className="mx-auto mb-2 opacity-60" />
          <div className="mb-1 text-sm font-medium">No results found</div>
          <div className="text-xs opacity-80">Try different keywords or check spelling</div>
        </div>
      ) : null}

      {filteredResults.length > 0 && (
        <div
          id="search-help-text"
          data-testid="search-help-text"
          className="search-help-text flex items-center justify-between rounded-b-lg border-t border-slate-100 bg-slate-50 px-3.5 py-2 text-[10px] font-medium text-slate-500"
        >
          <div className="flex items-center gap-1.5">
            <span className="rounded bg-slate-200 px-1.5 py-px text-[9px] font-semibold">Up/Down</span>
            Navigate
          </div>
          <div className="flex items-center gap-2.5">
            <div className="flex items-center gap-1">
              <span className="rounded bg-slate-200 px-1.5 py-px text-[9px] font-semibold">Enter</span>
              Select
            </div>
            <div className="flex items-center gap-1">
              <span className="rounded bg-slate-200 px-1.5 py-px text-[9px] font-semibold">Esc</span>
              Close
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <header
      id="education-header"
      data-testid="education-header"
      className="education-header"
      style={{
        position: 'fixed',
        top: 'env(safe-area-inset-top, 0px)',
        left: 0,
        width: '100%',
        zIndex: 100,
        backgroundColor: 'white',
        height: '70px',
        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.08)',
        borderBottom: '1px solid #f1f5f9'
      }}
    >
      <div
        id="header-main-container"
        data-testid="header-main-container"
        className="header-main-container mx-auto flex h-[70px] max-w-[1300px] items-center justify-between gap-2 px-5"
      >
        <div id="header-left-section" className="header-left-section flex shrink-0 items-center gap-6">
          <img
            loading="lazy"
            id="site-logo"
            data-testid="site-logo"
            className="site-logo"
            src={logoUrl || KRUMAGO.IMAGES.AVPS}
            alt="Logo"
            style={{
              width: "100px",
              height: "auto",
              cursor: 'pointer',
              objectFit: 'contain'
            }}
            onClick={() => handleNavigation('/')}
          />

          <nav id="main-navigation" className="main-navigation hidden items-center gap-2 lg:flex">
            {NAV_ITEMS.map((item) => (
              <button
                key={item.path}
                type="button"
                id={`nav-${item.label.toLowerCase()}`}
                data-testid={`nav-${item.label.toLowerCase()}`}
                className={`nav-item nav-${item.label.toLowerCase()}`}
                onClick={() => handleNavigation(item.path)}
                style={navButtonStyle(item.path)}
              >
                {item.label}
              </button>
            ))}
          </nav>
        </div>

        <button
          type="button"
          id="mobile-nav-toggle"
          data-testid="mobile-nav-toggle"
          className="mobile-nav-toggle ml-auto inline-flex shrink-0 items-center justify-center rounded-md border border-slate-200 bg-slate-50 p-2 lg:hidden"
          onClick={() => setOpenMenu(true)}
          aria-label="Open navigation"
        >
          <Menu size={22} color="var(--education-primary, #746EAC)" />
        </button>

        <div
          ref={searchContainerRef}
          id="global-search"
          data-testid="global-search"
          className="global-search relative mx-auto min-w-0 flex-1 max-w-[400px]"
        >
          <div
            id="search-input-container"
            data-testid="search-input-container"
            className="search-input-container relative rounded-lg border bg-white transition"
            style={{
              borderColor: isSearchFocused ? 'var(--education-primary, #746EAC)' : '#e5e7eb',
              boxShadow: isSearchFocused ? '0 2px 8px var(--education-primary-soft, rgba(116, 110, 172, 0.1))' : 'none',
            }}
          >
            <input
              ref={inputRef}
              id="course-search-input"
              data-testid="course-search-input"
              className="course-search-input w-full rounded-lg border-0 bg-transparent py-2.5 pl-3.5 pr-[92px] text-sm font-medium text-gray-700 outline-none placeholder:text-gray-400"
              placeholder="Search courses, categories..."
              value={searchQuery}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onFocus={handleSearchFocus}
              onBlur={handleSearchBlur}
            />

            {loading && (
              <span
                id="search-loader"
                data-testid="search-loader"
                className="search-loader absolute top-1/2 -translate-y-1/2"
                style={{ right: '55px' }}
              >
                <Loader2 size={16} className="animate-spin" color="var(--education-secondary, #3B9394)" />
              </span>
            )}

            <span className="absolute right-2 top-1/2 flex -translate-y-1/2 items-center gap-1">
              {searchQuery.length > 0 && (
                <button
                  type="button"
                  id="search-clear-btn"
                  data-testid="search-clear-btn"
                  className="search-clear-btn inline-flex h-7 w-7 items-center justify-center rounded-md bg-gray-100 transition hover:bg-gray-200"
                  onClick={handleClearSearch}
                  aria-label="Clear search"
                >
                  <X size={16} color="#6b7280" />
                </button>
              )}
              <button
                type="button"
                id="search-action-btn"
                data-testid="search-action-btn"
                className="search-action-btn inline-flex h-8 w-8 items-center justify-center rounded-md text-white transition"
                onClick={handleSearchClick}
                aria-label="Search courses"
                style={{ background: 'var(--education-secondary, #3B9394)' }}
              >
                <Search size={18} />
              </button>
            </span>
          </div>

          <SearchDropdown />
        </div>

        <div id="header-actions" className="header-actions ml-auto flex shrink-0 items-center gap-1.5">
          {isLoggedIn ? (
            <Link
              to="/user-profile"
              id="user-profile-link"
              data-testid="user-profile-link"
              className="user-profile-link"
              aria-label="Profile"
              title="Profile"
              style={{ textDecoration: 'none' }}
            >
              <span
                id="nav-profile"
                data-testid="nav-profile"
                className="nav-profile inline-flex h-10 items-center justify-center gap-2 rounded-lg px-3 transition"
                style={{ background: 'var(--education-secondary, #3B9394)', color: '#ffffff', fontSize: 14, fontWeight: 700, whiteSpace: 'nowrap' }}
              >
                <User size={20} color="white" />
                <span>Profile</span>
              </span>
            </Link>
          ) : (
            <button
              type="button"
              id="desktop-login-button"
              data-testid="desktop-login-button"
              className="auth-signin-btn"
              onClick={handleSignInClick}
              style={{
                background: 'var(--education-secondary, #3B9394)',
                borderRadius: '8px',
                color: 'white',
                padding: '10px 20px',
                fontSize: '14px',
                fontWeight: 600,
                border: 'none',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                minWidth: '90px',
                transition: 'all 0.2s ease',
                letterSpacing: '0.3px'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'var(--education-secondary-dark, #2d7a7b)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'var(--education-secondary, #3B9394)';
              }}
            >
              Sign In
            </button>
          )}
        </div>
      </div>

      {openMenu && (
        <div
          id="mobile-navigation"
          data-testid="mobile-navigation"
          className="mobile-navigation fixed inset-0 z-[150] lg:hidden"
        >
          <button
            type="button"
            className="absolute inset-0 h-full w-full bg-black/40"
            onClick={() => setOpenMenu(false)}
            aria-label="Close navigation"
          />
          <aside
            id="mobile-nav-content"
            className="mobile-nav-content relative h-full w-[280px] bg-white p-4 shadow-xl"
            role="dialog"
            aria-modal="true"
          >
            <div id="mobile-nav-header" className="mobile-nav-header mb-5 flex items-center justify-between">
              <img
                loading="lazy"
                id="mobile-logo"
                data-testid="mobile-logo"
                className="mobile-logo"
                src={logoUrl || KRUMAGO.IMAGES.AVPS}
                alt="Logo"
                style={{ width: '90px', height: 'auto', cursor: 'pointer' }}
                onClick={() => handleNavigation('/')}
              />
              <button
                type="button"
                id="mobile-nav-close"
                data-testid="mobile-nav-close"
                className="mobile-nav-close inline-flex items-center justify-center rounded-md bg-gray-100 p-1.5 transition hover:bg-gray-200"
                onClick={() => setOpenMenu(false)}
                aria-label="Close navigation"
              >
                <X size={18} color="var(--education-primary, #746EAC)" />
              </button>
            </div>

            <nav id="mobile-nav-menu" className="mobile-nav-menu flex flex-col">
              {NAV_ITEMS.map((item) => (
                <button
                  key={item.path}
                  type="button"
                  id={`mobile-nav-item-${item.label.toLowerCase()}`}
                  data-testid={`mobile-nav-item-${item.label.toLowerCase()}`}
                  className={`mobile-nav-item mobile-nav-${item.label.toLowerCase()}`}
                  onClick={() => handleNavigation(item.path)}
                  style={mobileNavButtonStyle(item.path)}
                >
                  {item.label}
                </button>
              ))}

              <button
                type="button"
                id="mobile-nav-cart"
                data-testid="mobile-nav-cart"
                className="mobile-nav-item mobile-nav-cart"
                onClick={() => handleNavigation('/cart')}
                style={mobileNavButtonStyle('/cart')}
              >
                <span
                  id="mobile-cart-badge"
                  data-testid="mobile-cart-badge"
                  className="mobile-cart-badge relative mr-3 inline-flex items-center justify-center"
                >
                  <ShoppingCart size={18} color="var(--education-primary, #746EAC)" />
                  {cartItemCount > 0 && (
                    <span
                      className="absolute -right-2 -top-2 inline-flex min-w-[18px] items-center justify-center rounded-full border-2 border-white px-1 text-[10px] font-bold text-white"
                      style={{
                        background: 'var(--education-secondary, #3B9394)',
                        height: '18px',
                      }}
                    >
                      {cartItemCount}
                    </span>
                  )}
                </span>
                Cart
              </button>
            </nav>
          </aside>
        </div>
      )}

      {snackbarOpen && snackbarMessage && (
        <div
          id="header-notification"
          data-testid="header-notification"
          className="header-notification fixed left-1/2 top-4 z-[200] w-[calc(100%-2rem)] max-w-md -translate-x-1/2"
        >
          <div
            id="notification-alert"
            data-testid="notification-alert"
            className="notification-alert flex items-start justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700 shadow-lg"
            role="alert"
          >
            <span>{snackbarMessage}</span>
            <button
              type="button"
              className="shrink-0 rounded p-0.5 text-red-600 transition hover:bg-red-100"
              onClick={handleCloseSnackbar}
              aria-label="Dismiss notification"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}
    </header>
  );
};

export default EducationDomainHeader;
